import { useState, useEffect } from 'react';
import type { DeadlinesConfig } from '@vedjegy/shared';

interface Props {
  /** 'application' shows applicationStart/End; 'review' shows reviewStart/End */
  type: 'application' | 'review';
  deadlines: DeadlinesConfig;
}

function formatCountdown(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const days  = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const mins  = totalMinutes % 60;
  if (days > 0)  return `${days} nap ${hours} óra`;
  if (hours > 0) return `${hours} óra ${mins} perc`;
  return `${mins} perc`;
}

export default function DeadlineBanner({ type, deadlines }: Props) {
  const [now, setNow] = useState(() => Date.now());

  // Refresh every minute so the countdown stays accurate
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const startISO = type === 'application' ? deadlines.applicationStart : deadlines.reviewStart;
  const endISO   = type === 'application' ? deadlines.applicationEnd   : deadlines.reviewEnd;

  // No dates configured → nothing to show
  if (!startISO && !endISO) return null;

  const startMs = startISO ? new Date(startISO).getTime() : null;
  const endMs   = endISO   ? new Date(endISO).getTime()   : null;

  const label = type === 'application' ? 'Pályázatbeadási' : 'Bírálati';

  // Not yet open
  if (startMs !== null && now < startMs) {
    const remaining = startMs - now;
    return (
      <Banner color="blue">
        🕐 {label} időszak <strong>{formatCountdown(remaining)}</strong> múlva nyílik meg.
      </Banner>
    );
  }

  // Open, show remaining time until end
  if (endMs !== null && now < endMs) {
    const remaining = endMs - now;
    const urgent = remaining < 24 * 60 * 60 * 1000; // less than 24h
    return (
      <Banner color={urgent ? 'red' : 'amber'}>
        ⏱ {label} határidő: még <strong>{formatCountdown(remaining)}</strong>.
        &nbsp;({new Date(endMs).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' })})
      </Banner>
    );
  }

  // Deadline passed
  if (endMs !== null && now >= endMs) {
    return (
      <Banner color="red">
        ⚠ {label} határidő lejárt ({new Date(endMs).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' })}).
      </Banner>
    );
  }

  // Start passed, no end set — window is open indefinitely
  return null;
}

// ---------------------------------------------------------------------------
// Internal Banner display helper
// ---------------------------------------------------------------------------
type BannerColor = 'blue' | 'amber' | 'red';

const BANNER_STYLES: Record<BannerColor, React.CSSProperties> = {
  blue:  { background: 'rgba(59,130,246,.10)',  border: '1px solid rgba(59,130,246,.35)',  color: '#1d4ed8' },
  amber: { background: 'rgba(234,179,8,.10)',   border: '1px solid rgba(234,179,8,.40)',   color: '#92400e' },
  red:   { background: 'rgba(239,68,68,.10)',   border: '1px solid rgba(239,68,68,.35)',   color: '#b91c1c' },
};

function Banner({ color, children }: { color: BannerColor; children: React.ReactNode }) {
  return (
    <div style={{
      ...BANNER_STYLES[color],
      borderRadius: 'var(--radius-md)',
      padding: '12px 16px',
      fontSize: 14,
      marginBottom: 20,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}
